import React, { useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";

import TextAreaField from "./TextAreaField";

const API_URL = process.env.REACT_APP_API_URL;

const RutinaForm = ({ cat, onClose, onRutinaAdded }) => {
  const [inputRutinas, setInputRutinas] = useState("");
  const [rutinas, setRutinas] = useState(cat?.rutinas || []);
  const [isLoading, setIsLoading] = useState(false);

  const token = localStorage.getItem("token");

  const planActual = localStorage.getItem("planActual") || "FREE";
  const isFreePlan = planActual === "FREE";

  /** ➕ Agregar una o varias rutinas **/
  const handleAddRutinas = async (e) => {
    e.preventDefault();

    const nuevas = inputRutinas
      .split("\n")
      .map((r) => r.trim())
      .filter((r) => r !== "");

    if (nuevas.length === 0) {
      toast.warning("Escribe al menos una rutina antes de agregar");
      return;
    }

    try {
      setIsLoading(true);
      const res = await axios.post(
        `${API_URL}/cat/${cat._id}/rutinas`,
        { rutina: nuevas },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const updated = res.data?.rutinas || [...rutinas, ...nuevas];
      setRutinas(updated);
      setInputRutinas("");
      toast.success("Rutinas agregadas correctamente ✅");
      if (onRutinaAdded) onRutinaAdded();
    } catch (error) {
      toast.error("Error al agregar rutinas");
    } finally {
      setIsLoading(false);
    }
  };

  /** 🗑️ Eliminar rutina **/
  const handleDeleteRutina = async (rutina) => {
    if (!window.confirm(`¿Eliminar la rutina "${rutina}"?`)) return;

    try {
      const res = await axios.delete(`${API_URL}/cat/${cat._id}/rutinas`, {
        data: { rutina },
        headers: { Authorization: `Bearer ${token}` },
      });

      const updated = res.data?.rutinas || rutinas.filter((r) => r !== rutina);

      setRutinas(updated);
      toast.info("Rutina eliminada 🧽");
      if (onRutinaAdded) onRutinaAdded();
    } catch (error) {
      toast.error("No se pudo eliminar la rutina");
    }
  };

  const handleGenerarIA = async () => {
    if (!cat?.name) {
      toast.warning("La categoría debe estar definida para generar rutinas");
      return;
    }

    try {
      setIsLoading(true);
      const res = await axios.post(
        `${API_URL}/ia/rutinas/generar/${cat._id}`,
        { categoria: cat.name },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const rutinasConSaltos = res.data.rutinas.join("\n");
      setInputRutinas(rutinasConSaltos);
      toast.success("Rutinas generadas con IA ✅");
    } catch (error) {
      toast.error("No se pudieron generar rutinas con IA");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      {/* Modal responsive con scroll interno */}
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                🧰 Rutinas de {cat?.name || "Categoría"}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            {/* Listado de rutinas */}
            {rutinas.length > 0 ? (
              <ul className="border rounded-lg p-3 bg-gray-50 max-h-48 overflow-y-auto mb-4">
                {rutinas.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-sm text-gray-700 border-b last:border-none py-1"
                  >
                    <span className="pr-2">• {r}</span>
                    <button
                      onClick={() => handleDeleteRutina(r)}
                      className="text-red-500 hover:text-red-700"
                      title="Eliminar rutina"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 italic mb-4">
                No hay rutinas registradas aún.
              </p>
            )}

            {/* Formulario */}
            <form onSubmit={handleAddRutinas} className="space-y-4">
              <TextAreaField
                label="Nueva(s) rutina(s)"
                value={inputRutinas}
                onChange={(e) => setInputRutinas(e.target.value)}
                rows={4}
                placeholder={`Ejemplo:\nRevisión de válvulas\nLimpieza óptica\nVerificación de voltaje`}
              />

              <p className="text-xs text-gray-500">
                Puedes escribir varias rutinas separadas por saltos de línea.
              </p>

              {/* IA */}
              <div className="relative inline-block">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (!isFreePlan) handleGenerarIA();
                  }}
                  disabled={isFreePlan || isLoading}
                  className={
                    isFreePlan
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : ""
                  }
                >
                  {isLoading ? "Generando..." : "Generar con IA"}
                </Button>

                {isFreePlan && (
                  <span className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-[10px] px-2 py-0.5 rounded-full shadow">
                    Solo planes de pago
                  </span>
                )}
              </div>

              {/* Acciones */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  type="button"
                  className="w-full sm:w-auto"
                >
                  Cerrar
                </Button>
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? "Guardando..." : "Agregar rutinas"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RutinaForm;
